import {
  Building2,
  Users,
  Calendar,
  DollarSign,
  Award,
  Heart,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { StatCard } from "@/components/dashboard/StatCard";
import { RecentActivity } from "@/components/dashboard/RecentActivity";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import { DashboardService } from "@/services/APIs";
import MMNavigationRoutes from "../utils/navigationRoutes";

// Helper function to transform graph data from API format to chart format
// Always show all 12 months, fill missing months with 0
const transformGraphData = (
  graphData: { [key: string]: number },
  valueKey: string
) => {
  const allMonths = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Convert API keys like "Nov 2025" → "Nov"
  const parsedData: { [key: string]: number } = {};
  Object.entries(graphData).forEach(([key, value]) => {
    const shortMonth = key.substring(0, 3); // "Nov", "Dec"
    parsedData[shortMonth] = value;
  });

  // Build full graph with missing months = 0
  return allMonths.map(month => ({
    month,
    [valueKey]: parsedData[month] ?? 0,
  }));
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const data = await DashboardService.getDashboardData(false);
        setDashboardData(data);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const handleMembersClick = () => {
    navigate("/mm" + MMNavigationRoutes.MEMBER_LIST);
  };

  const handleEventsClick = () => {
    navigate("/mm" + MMNavigationRoutes.EVENTS_LIST);
  };

  // Transform graph data for charts - shows only months from API response
  const donationData = dashboardData?.donation_graph
    ? transformGraphData(dashboardData.donation_graph, "amount")
    : [];

  const memberGrowthData = dashboardData?.member_graph
    ? transformGraphData(dashboardData.member_graph, "members")
    : [];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
            <p className="mt-1 text-muted-foreground">
              Welcome back! Here&apos;s your platform overview.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Loading dashboard data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
          <p className="mt-1 text-muted-foreground">
            Welcome back! Here&apos;s your platform overview.
          </p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <StatCard
          title="Total Organizations"
          value={dashboardData?.organizations ?? "0"}
          change="+12%"
          changeType="increase"
          icon={Building2}
          iconColor="text-info"
        />
        <StatCard
          title="Total Members"
          value={dashboardData?.members ?? "0"}
          change="+18%"
          changeType="increase"
          icon={Users}
          iconColor="text-accent"
          onClick={handleMembersClick}
        />
        <StatCard
          title="Active Events"
          value={dashboardData?.active_events ?? "0"}
          change="+5"
          changeType="increase"
          icon={Calendar}
          iconColor="text-info"
          onClick={handleEventsClick}
        />
        <StatCard
          title="Donations Received"
          value={dashboardData?.donations ?? "$0"}
          change="+23%"
          changeType="increase"
          icon={DollarSign}
          iconColor="text-success"
        />
        <StatCard
          title="Active Sponsorships"
          value={dashboardData?.sponsors ?? "0"}
          change="+2"
          changeType="increase"
          icon={Award}
          iconColor="text-warning"
        />
        <StatCard
          title="Volunteers Active"
          value={dashboardData?.volunteers ?? "0"}
          change="+34"
          changeType="increase"
          icon={Heart}
          iconColor="text-destructive"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Donation Trends</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={donationData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="month" className="text-xs" />
                <YAxis className="text-xs" />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="amount"
                  stroke="#3c83f6"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Member Growth</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={memberGrowthData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="month" className="text-xs" />
                <YAxis className="text-xs" />
                <Tooltip />
                <Bar dataKey="members" fill="#16a249" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
      <RecentActivity activities={dashboardData?.recent_activities ?? []} />
    </div>
  );
};

export default Dashboard;
